import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { RiMoonLine, RiSunLine } from 'react-icons/ri';

const NAV_LINKS = [
    { label: 'Features', href: '/#features' },
    { label: 'Solutions', href: '/#solutions' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'About', href: '/#about' },
];

export default function Navbar() {
    const { isDarkMode, toggleDarkMode } = useTheme();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeLink, setActiveLink] = useState('');
    const menuRef = useRef(null);

    /* scroll */
    useEffect(() => {
        const h = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', h, { passive: true });
        return () => window.removeEventListener('scroll', h);
    }, []);

    /* close on outside click */
    useEffect(() => {
        if (!menuOpen) return;
        const h = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [menuOpen]);

    /* lock body scroll */
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    return (
        <>
            <style>{`
        .nb-link {
          position: relative;
          color: ${isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.85)'};
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.2px;
          text-decoration: none;
          transition: color 0.25s ease;
          padding: 2px 0;
        }
        .nb-link::after {
          content: '';
          position: absolute;
          bottom: -2px; left: 0; right: 0;
          height: 1px;
          background: ${isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'};
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.25s ease;
          border-radius: 1px;
        }
        .nb-link:hover, .nb-link.active { color: ${isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'}; }
        .nb-link:hover::after, .nb-link.active::after { transform: scaleX(1); }

        .nb-cta {
          color: ${isDarkMode ? 'white' : 'white'};
          background: ${isDarkMode ? 'transparent' : '#c41e1e'};
          font-size: 13px;
          font-weight: 500;
          padding: 7px 18px;
          border-radius: 100px;
          text-decoration: none;
          letter-spacing: 0.2px;
          border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : 'transparent'};
          transition: border-color 0.25s, background 0.25s;
          white-space: nowrap;
        }
        .nb-cta:hover {
          border-color: ${isDarkMode ? 'rgba(255,255,255,0.28)' : 'transparent'};
          background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : '#a91818'};
        }

        .nb-signin {
          color: ${isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.85)'};
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.2px;
          text-decoration: none;
          transition: color 0.25s;
          white-space: nowrap;
        }
        .nb-signin:hover { color: ${isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'}; }

        /* ── Mobile overlay ─────────────────────────── */
        .nb-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.55);
          backdrop-filter: blur(4px);
          z-index: 49;
          opacity: 0; pointer-events: none;
          transition: opacity 0.22s ease;
        }
        .nb-overlay.open { opacity: 1; pointer-events: auto; }

        /* ── Mobile drawer ──────────────────────────── */
        .nb-drawer {
          position: fixed; top: 0; right: 0;
          width: min(320px, 86vw); height: 100dvh;
          background: ${isDarkMode ? 'rgba(10,10,10,0.96)' : 'rgba(255,255,255,0.96)'};
          backdrop-filter: blur(24px);
          border-left: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};
          z-index: 60;
          transform: translateX(100%);
          transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
          display: flex; flex-direction: column;
        }
        .nb-drawer.open { transform: translateX(0); }

        .nb-mlink {
          display: flex; align-items: center; gap: 10px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.85)'};
          font-size: 13.5px; font-weight: 400; letter-spacing: 0.2px;
          text-decoration: none;
          padding: 13px 28px;
          border-bottom: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
          transition: color 0.2s, background 0.2s, padding-left 0.2s;
        }
        .nb-mlink:hover {
          color: ${isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'};
          background: ${isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'};
          padding-left: 34px;
        }

        /* ── Hamburger ──────────────────────────────── */
        .hb-line {
          display: block; height: 1.5px; width: 100%;
          background: ${isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)'};
          border-radius: 2px;
          transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
          transform-origin: center;
        }
        .hb.open .hb-line:nth-child(1) { transform: translateY(5.5px) rotate(45deg); }
        .hb.open .hb-line:nth-child(2) { transform: scaleX(0); opacity: 0; }
        .hb.open .hb-line:nth-child(3) { transform: translateY(-5.5px) rotate(-45deg); }

        .h-btn-ghost {
          display: inline-flex; align-items: center; gap: 8px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.8)'};
          transition: background 0.25s, color 0.25s;
        }
        .theme-toggle-btn:hover {
          background: ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'};
          color: ${isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'};
        }
      `}</style>

            {/* Mobile overlay */}
            <div className={`nb-overlay ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)} />

            {/* ── Outer wrapper ─ handles the floating inset when scrolled ─ */}
            <div
                className="fixed inset-x-0 top-0 z-50 pointer-events-none"
                style={{
                    padding: scrolled ? '18px 24px 0' : '0',
                    transition: 'padding 0.45s cubic-bezier(0.4,0,0.2,1)',
                }}
            >
                {/* ── Nav pill / bar ────────────────────────────────────────── */}
                <nav
                    className="pointer-events-auto w-full mx-auto flex items-center"
                    style={{
                        height: scrolled ? '68px' : '72px',
                        maxWidth: scrolled ? '1200px' : '100%',
                        borderRadius: scrolled ? '30px' : '0',
                        background: scrolled
                            ? isDarkMode ? 'rgba(13,13,13,0.78)' : 'rgba(255,255,255,0.85)'
                            : 'transparent',
                        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
                        border: scrolled
                            ? isDarkMode ? '1px solid rgba(255,255,255,0.075)' : '1px solid rgba(0,0,0,0.08)'
                            : '1px solid transparent',
                        boxShadow: scrolled
                            ? isDarkMode
                                ? '0 2px 20px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.03) inset'
                                : '0 4px 24px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02) inset'
                            : 'none',
                        padding: scrolled ? '0 28px' : '0 40px',
                        transition: 'all 0.45s cubic-bezier(0.4,0,0.2,1)',
                    }}
                >
                    {/* Logo + brand name */}
                    <Link to="/" className="flex items-center gap-2.5 shrink-0 group" onClick={() => setMenuOpen(false)}>
                        <img
                            src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                            alt="QuadProperties"
                            className="w-auto object-contain transition-all duration-300 group-hover:opacity-85"
                            style={{ height: scrolled ? '28px' : '32px', transition: 'height 0.4s cubic-bezier(0.4,0,0.2,1)' }}
                        />
                        <span className="hidden sm:inline" style={{
                            fontSize: 13,
                            fontWeight: 400,
                            color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.85)',
                            letterSpacing: '-0.2px',
                            transition: 'opacity 0.3s',
                            whiteSpace: 'nowrap',
                        }}>Quadcores Properties</span>
                    </Link>

                    {/* Desktop center links */}
                    <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
                        {NAV_LINKS.map(({ label, href }) => (
                            <a
                                key={label}
                                href={href}
                                className={`nb-link ${activeLink === href ? 'active' : ''}`}
                                onClick={() => setActiveLink(href)}
                            >
                                {label}
                            </a>
                        ))}
                    </div>

                    {/* Desktop right CTAs */}
                    <div className="hidden md:flex items-center gap-5 shrink-0">
                        <button
                            onClick={toggleDarkMode}
                            className="theme-toggle-btn"
                            aria-label="Toggle theme"
                        >
                            {isDarkMode ? <RiSunLine size={18} /> : <RiMoonLine size={18} />}
                        </button>
                        <Link to="/login" className="nb-signin">Sign in</Link>
                        <Link to="/register" className="nb-cta">Get started →</Link>
                    </div>

                    {/* Mobile: sign-in + hamburger */}
                    <div className="flex md:hidden items-center gap-4 ml-auto">
                        <button
                            onClick={toggleDarkMode}
                            className="theme-toggle-btn"
                            aria-label="Toggle theme"
                        >
                            {isDarkMode ? <RiSunLine size={18} /> : <RiMoonLine size={18} />}
                        </button>
                        <Link to="/login" className="nb-signin">Sign in</Link>
                        <button
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            onClick={() => setMenuOpen(v => !v)}
                            className={`hb ${menuOpen ? 'open' : ''} flex flex-col gap-[5px] w-5 bg-transparent border-none cursor-pointer p-0`}
                        >
                            <span className="hb-line" />
                            <span className="hb-line" style={{ width: '70%', alignSelf: 'flex-end', background: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }} />
                            <span className="hb-line" />
                        </button>
                    </div>
                </nav>
            </div>

            {/* ── Mobile drawer ──────────────────────────────────────────── */}
            <div ref={menuRef} className={`nb-drawer ${menuOpen ? 'open' : ''}`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-7 py-5 border-b ${isDarkMode ? 'border-white/[0.05]' : 'border-black/[0.05]'}`}>
                    <div className="flex items-center gap-2.5">
                        <img src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"} alt="QuadProperties" className="h-6 w-auto opacity-80" />
                        <span style={{ fontSize: 12.5, fontWeight: 400, color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', letterSpacing: '-0.1px' }}>Quadcores Properties</span>
                    </div>
                    <button
                        onClick={() => setMenuOpen(false)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center border ${isDarkMode ? 'border-white/10 hover:bg-white/[0.06]' : 'border-black/10 hover:bg-black/[0.06]'} transition-colors`}
                        aria-label="Close"
                    >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1 1L9 9M9 1L1 9" stroke={isDarkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"} strokeWidth="1.4" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Links */}
                <div className="flex-1 overflow-y-auto py-2">
                    {NAV_LINKS.map(({ label, href }) => (
                        <a key={label} href={href} className="nb-mlink" onClick={() => setMenuOpen(false)}>
                            <span className={`w-1 h-1 rounded-full ${isDarkMode ? 'bg-white/15' : 'bg-black/15'} flex-shrink-0`} />
                            {label}
                        </a>
                    ))}
                </div>

                {/* Footer CTAs */}
                <div className={`p-5 border-t ${isDarkMode ? 'border-white/[0.05]' : 'border-black/[0.05]'} space-y-2.5`}>
                    <Link
                        to="/register"
                        className="nb-cta flex items-center justify-center w-full py-2.5 text-[13.5px]"
                        onClick={() => setMenuOpen(false)}
                    >
                        Get started →
                    </Link>
                    <Link
                        to="/login"
                        onClick={() => setMenuOpen(false)}
                        className={`flex items-center justify-center w-full py-2.5 rounded-full text-[13px] ${isDarkMode ? 'text-white/40 hover:text-white/70 border-white/[0.07] hover:border-white/15' : 'text-black/40 hover:text-black/70 border-black/[0.07] hover:border-black/15'} border transition-all duration-200`}
                    >
                        Sign in
                    </Link>
                </div>
            </div>
        </>
    );
}
